import { useQuery } from '@tanstack/react-query';
import type { Membership, PaginatedMembershipList } from 'api-client';
import { PaginatedMembershipListFromJSON } from 'api-client';
import { useMemo } from 'react';

import { apiGet } from '@/lib/http/client';
import { logger } from '@/lib/logger';

import { useUser } from '../allauth_auth/hooks.js';

const EMPTY_MEMBERS: readonly Membership[] = Object.freeze([]);
export const ORG_MEMBERS_QUERY_KEY = (
  userId: number | null | undefined,
  orgId: number | null | undefined
) => ['orgMembers', userId, orgId] as const;
const STALE_TIME_MS = 5 * 60 * 1000;
/** The memberships endpoint's max page size (apps/api/pagination.py). */
const MEMBERS_PAGE_SIZE = 100;

function getStatusCode(error: unknown): number | undefined {
  if (error && typeof error === 'object' && 'statusCode' in error) {
    const code = (error as { statusCode?: unknown }).statusCode;
    return typeof code === 'number' ? code : undefined;
  }
  return undefined;
}

/**
 * Lazily fetches the full member list for the given org.
 *
 * The query is disabled when `orgId` is null/undefined or when no user is
 * authenticated. Query results are cached for 5 minutes (matching the
 * `availableOrgs` cache in OrgContext) and scoped per-user to prevent
 * cross-account cache leakage on shared devices.
 *
 * Fetches EVERY page of memberships (the endpoint paginates — default page
 * size 20, max 100), so consumers such as the assignee/reassign pickers see
 * the full member list instead of silently hiding members of larger orgs and
 * making them unselectable.
 *
 * @param orgId - The numeric org ID to fetch members for.
 *   Hook is disabled (no fetch) when this is null or undefined, or when no
 *   authenticated user is available.
 * @returns `{ members, isLoading, isError, error, refetch }` — a stable object
 *   reference that updates only when query state changes. `refetch` re-runs the
 *   query (e.g. from a retry affordance after a fetch failure).
 *
 * @example
 * function MemberPicker({ orgId }: { orgId: number }) {
 *   const { members, isLoading } = useOrgMembers(orgId);
 *   if (isLoading) return <Spinner />;
 *   return <ul>{members.map(m => <li key={m.id}>{m.user.email}</li>)}</ul>;
 * }
 */
export function useOrgMembers(orgId: number | null | undefined) {
  const user = useUser() as { id?: number } | null;
  const userId = user?.id;

  const result = useQuery({
    queryKey: ORG_MEMBERS_QUERY_KEY(userId, orgId),
    queryFn: async (): Promise<Membership[]> => {
      try {
        // Walk every page. `page_size=100` is the endpoint's max, so this is
        // one request for orgs up to 100 members and a bounded handful beyond
        // that. Route through apiGet so shared error handling (e.g. the
        // org_access_paused 403 redirect in apiCall) runs for each request.
        const all: Membership[] = [];
        let page = 1;
        for (;;) {
          const json = await apiGet<unknown>(
            `/api/orgs/${orgId}/memberships/?page=${page}&page_size=${MEMBERS_PAGE_SIZE}`
          );
          const data: PaginatedMembershipList =
            PaginatedMembershipListFromJSON(json);
          all.push(...data.results);
          if (!data.next) {
            break;
          }
          page += 1;
        }
        return all;
      } catch (error) {
        logger.error('Failed to fetch org members', error as Error, {
          component: 'useOrgMembers',
          orgId,
          userId,
          status: getStatusCode(error),
        });
        throw error;
      }
    },
    staleTime: STALE_TIME_MS,
    enabled: !!(orgId && userId),
    retry: (failureCount: number, error: Error) => {
      // Skip 4xx (likely permission/missing org); allow 1 retry on 5xx.
      const status = getStatusCode(error);
      if (typeof status === 'number' && status >= 400 && status < 500) {
        return false;
      }
      return failureCount < 1;
    },
  });

  // Stable return wrapper so consumers comparing identity don't churn.
  // `result.refetch` is a stable reference from React Query, so including it in
  // the dependency array does not churn the memoized object.
  const { refetch } = result;
  return useMemo(
    () => ({
      members: result.data ?? (EMPTY_MEMBERS as Membership[]),
      isLoading: result.isLoading,
      isError: result.isError,
      error: (result.error as Error | null) ?? null,
      refetch,
    }),
    [result.data, result.isLoading, result.isError, result.error, refetch]
  );
}

/**
 * The signed-in user's CRM entity id (`ahoy_user`) in this org, or `undefined`
 * while the roster is loading / when the user has no member row.
 *
 * Every surface that gates on "am I the owner or creator of this record?"
 * needs this one join (auth user id → membership → entity id); resolving it
 * here keeps that lookup from being re-derived — and drifting — per surface.
 *
 * @param orgId - The numeric org ID. `undefined` while no org is resolved.
 */
export function useCurrentUserEntityId(
  orgId: number | null | undefined
): string | undefined {
  const user = useUser() as { id?: number } | null;
  const { members } = useOrgMembers(orgId);
  return members.find(m => m.userId === user?.id)?.entityId;
}
